import React, { useCallback, useMemo, useRef, useState } from 'react';
import { IconProps } from '../../../common/components/icon/Icon';
import { L } from '../../../abp/utils';
import IconButton from '../../../common/components/icon/IconButton';
import { ComboBox } from '@progress/kendo-react-dropdowns';


const PageButton: React.FC<{ onClick: () => void, iconName: IconProps['name'], iconStyle?: IconProps['style'], title?: string, disabled?: boolean }> =
    ({ iconName, iconStyle, onClick, title, disabled }) => {
        return <IconButton name={iconName} className="text-muted mx-auto my-1" onClick={() => onClick()}
            color="light" title={title} disabled={disabled} iconStyle={iconStyle} />
    };

const pageSizeData = [5, 10, 20, 50, 100];

const usePlmPagingUi = (initialPageSize: number = 5, initialPage: number = 0) => {

    const [pageSize, setPageSize] = useState(initialPageSize);
    const [page, setPage] = useState(Math.max(initialPage, 0));

    const totalCount = useRef(0);
    const pageCount = Math.ceil(totalCount.current / pageSize);
    const skipCount = page * pageSize;

    const pageData = useMemo(() => {
        const list = [];
        for (let index = 0; index < pageCount; index++) {
            list.push({ pageNumber: index + 1, pageIndex: index });
        }
        return list;
    }, [pageCount]);

    const inputWidth = `${pageCount.toString().length + 3}rem`;

    const resetAll = useCallback(()=>{
        setPageSize(initialPageSize);
        setPage(Math.max(initialPage, 0));
    },[initialPageSize, initialPage]);

    const reset = useCallback(()=>{        
        setPage(Math.max(initialPage, 0));
    },[initialPage]);

    const pagingControl = useCallback(() => {
        return <>
            <div className="row align-items-center justify-content-center">
                <div className="col-xs-12 col-lg-10 col-xl-8 col-xxl-6 row">
                    <div className="paging-controls m-2 col-md-auto col-xs-12 d-flex justify-content-center border-end border-1">
                        <div className="flex-1">
                            <PageButton onClick={() => setPage(0)} iconName="icon-double-right" iconStyle={{ transform: 'scaleX(-1)' }}
                                title={L('GoToFirstPage')} disabled={page < 1} />

                            <PageButton onClick={() => setPage(p => p - 1)} iconName="icon-forward" iconStyle={{ transform: 'scaleX(-1)' }}
                                title={L('GoToPreviousPage')} disabled={page < 1} />

                            <ComboBox data={pageData} textField='pageNumber' dataItemKey='pageIndex' value={pageData.find(p => p.pageIndex === page)}
                                onChange={v => v.value && setPage(v.value.pageIndex)} clearButton={false} style={{ width: inputWidth, minWidth: inputWidth, maxWidth: inputWidth }} />

                            <span className='ms-2'>/ {pageCount}</span>

                            <PageButton onClick={() => setPage(p => p + 1)} iconName="icon-forward"
                                title={L('GoToNextPage')} disabled={page >= pageCount - 1} />

                            <PageButton onClick={() => setPage(pageCount - 1)} iconName="icon-double-right"
                                title={L('GoToLastPage')} disabled={page >= pageCount - 1} />

                        </div>
                    </div>

                    <div className="paging-options m-2 col border-end border-1">
                        <span className="me-2">
                            {L('ItemsPerPage')}:
                        </span>
                        <ComboBox data={pageSizeData} value={pageSize} onChange={v => { if (!v.value) return; setPageSize(v.value); setPage(0) }}
                            clearButton={false} style={{ width: '5rem', minWidth: '5rem', maxWidth: '5rem' }} />
                    </div>

                    <div className="text-center col border border-0">{totalCount.current} Elemente</div>
                </div>
            </div>
        </>
    }, [page, pageCount, totalCount, inputWidth, pageData, pageSize]);

    return { takeCount: pageSize, skipCount, totalCount, pagingControl, reset, resetAll };
};

export default usePlmPagingUi;